/* import __COLOCATED_TEMPLATE__ from './bot-email-reply.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import type Conversation from 'embercom/objects/inbox/conversation';
import type BotComment from 'embercom/objects/inbox/renderable/bot-comment';
import type FinAnswer from 'embercom/objects/inbox/renderable/fin-answer';
import AdminSummary from 'embercom/objects/inbox/admin-summary';
import { type TableEntryHighlights } from 'embercom/objects/inbox/conversation-table-entry';
import { type RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  getAllTranslationLanguageCodesFromPartGroup,
  getOriginalLanguageCodeFromPartGroup,
  getLanguageLabelFromCode,
  translatedPartExists,
} from '../helpers/translations';
import {
  normalizeLocale,
  nextInCycle,
  getDefaultWorkspaceLocale,
} from '../helpers/language-selection';
import { computeTranslationUi } from '../helpers/translation-ui';
import { shouldRouteToAutomation } from '../helpers/ai-agent-routing';
import {
  INBOUND_EMAIL_SUPPORTED_TAGS,
  INBOUND_EMAIL_SUPPORTED_ATTRIBUTES,
} from 'embercom/lib/channels/constants';

interface BotCommentRenderablePart extends RenderablePart {
  renderableType: RenderableType.BotComment;
  renderableData: BotComment;
}

interface FinAnswerRenderablePart extends RenderablePart {
  renderableType: RenderableType.FinAnswer;
  renderableData: FinAnswer;
}

type BotEmailReplyRenderablePart = BotCommentRenderablePart | FinAnswerRenderablePart;

export interface BotEmailReplyPartGroup extends PartGroup {
  parts: Array<BotEmailReplyRenderablePart>;
}

interface Args {
  partGroup: BotEmailReplyPartGroup;
  conversation: Conversation;
  isReadOnly: boolean;
  isTicketStream: boolean;
  tinyAvatar?: boolean;
  highlightedConversationPartId?: number;
  highlights?: TableEntryHighlights;
  isStandalone?: boolean;
}

interface Signature {
  Args: Args;
}

export default class ConversationStreamPartGroupsBotEmailReplyComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare frontendStatsService: any;

  supportedTags = INBOUND_EMAIL_SUPPORTED_TAGS;
  supportedAttributes = INBOUND_EMAIL_SUPPORTED_ATTRIBUTES;

  @tracked selectedLangCode?: string;

  get availableTranslationCodes(): string[] {
    return getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup);
  }

  get originalLanguageCode(): string | undefined {
    return getOriginalLanguageCodeFromPartGroup(this.args.partGroup);
  }

  get defaultLanguageCode(): string | undefined {
    return getDefaultWorkspaceLocale(this.session);
  }

  private get translationUi() {
    return computeTranslationUi({
      session: this.session,
      available: this.availableTranslationCodes,
      preferred: this.session?.teammate?.teammatePreferredLocale,
      defaultLang: this.defaultLanguageCode,
      original: this.originalLanguageCode,
      selectedLangCode: this.selectedLangCode,
    });
  }

  get cycleOrderCodes(): string[] {
    return this.translationUi.cycleOrderCodes;
  }

  get currentSelectedCode(): string | undefined {
    return this.translationUi.currentSelectedCode;
  }

  get renderableData() {
    return this.args.partGroup.parts.lastObject!.renderableData;
  }

  get avatarObject(): AdminSummary {
    if (this.workflowSummary) {
      return new AdminSummary(
        parseInt(this.workflowSummary.id, 10),
        this.intl.t('inbox.custom-bot', { name: this.workflowSummary.title }),
        this.renderableData.entity.imageURL,
      );
    } else {
      return this.renderableData.creatingEntity.object;
    }
  }

  private get shouldRouteAvatarToAutomation(): boolean {
    return shouldRouteToAutomation(this.session.workspace, this.avatarObject);
  }

  get avatarRoute() {
    if (this.workflowSummary) {
      return 'apps.app.operator.custom-bots.custom-bot.show';
    } else if (this.shouldRouteAvatarToAutomation) {
      return 'apps.app.settings.ai-automation.automation';
    } else {
      return 'apps.app.admins.admin';
    }
  }

  get workflowSummary() {
    return 'customBotSummary' in this.renderableData && this.renderableData.customBotSummary;
  }

  get lastPartSeenByUser() {
    let { conversation } = this.args;

    return (
      conversation?.lastSeenByUserAt &&
      conversation?.lastSeenByUserAt >= this.lastPartInGroup.createdAt
    );
  }

  get lastPartInGroup() {
    let { parts } = this.args.partGroup;
    return parts[parts.length - 1];
  }

  get currentTranslatablePart() {
    return this.args.partGroup.parts.find((p) => p.renderableType === 7 || p.renderableType === 58);
  }

  get autoTranslationEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get showOriginalMessage(): boolean {
    if (!this.translatedPartExists) {
      return true;
    }
    // When auto-translation is disabled, show the original message
    if (!this.autoTranslationEnabled) {
      return true;
    }
    return normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode);
  }

  get showTranslatedMessage() {
    return !this.showOriginalMessage;
  }

  get translatedPartExists() {
    return translatedPartExists(this.args.partGroup);
  }

  get conversationLanguageCode(): string | undefined {
    let raw = this.args.conversation?.getAttributeForDescriptor('language')?.value as
      | string
      | undefined;
    return normalizeLocale(typeof raw === 'string' ? raw : undefined);
  }

  get conversationLanguage(): string {
    // Try conversation language attribute first, then fall back to block translations, then workspace locale
    let code =
      this.conversationLanguageCode ||
      getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup)[0] ||
      this.session.workspace?.locale;
    return code ? getLanguageLabelFromCode(code, this.intl.primaryLocale) || '' : '';
  }

  get selectedTranslationLanguageCode(): string | undefined {
    if (!this.autoTranslationEnabled) {
      return undefined;
    }
    return this.currentSelectedCode;
  }

  @action recordEmailRendered(): void {
    this.frontendStatsService.enqueueInteractionMetric({
      name: 'email_rendered',
      email_direction: 'outbound_bot',
    });
    this.frontendStatsService.sendInteractionMetrics();
  }

  @action
  toggleTranslation() {
    let next = nextInCycle(this.currentSelectedCode, this.cycleOrderCodes);
    if (next) {
      this.selectedLangCode = next;
    }
  }

  partHasTranslations(part: BotEmailReplyRenderablePart) {
    let singlePartGroup = { parts: [part] } as unknown as PartGroup;
    return translatedPartExists(singlePartGroup);
  }

  nextPartIsRelated = (part: BotEmailReplyRenderablePart) => {
    let { parts } = this.args.partGroup;
    let index = parts.indexOf(part);
    if (index === parts.length - 1) {
      return false;
    }
    let nextPart = parts[index + 1];
    return (
      'relatedPartId' in nextPart.renderableData &&
      nextPart.renderableData.relatedPartId === part.entityId
    );
  };

  previousPartIsRelated = (part: BotEmailReplyRenderablePart) => {
    let { parts } = this.args.partGroup;
    let index = parts.indexOf(part);
    return (
      index > 0 &&
      'relatedPartId' in part.renderableData &&
      parts[index - 1].entityId === part.renderableData.relatedPartId
    );
  };

  isRatingRequest = (part: BotEmailReplyRenderablePart) => {
    return 'isRatingRequest' in part.renderableData && part.renderableData.isRatingRequest;
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::BotEmailReply': typeof ConversationStreamPartGroupsBotEmailReplyComponent;
    'inbox2/conversation-stream/part-groups/bot-email-reply': typeof ConversationStreamPartGroupsBotEmailReplyComponent;
  }
}
